import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import debounce from "lodash/debounce";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { InfiniteScrollConceptAutocomplete } from "../../../components/RegulationsTable/FilterBar/InfiniteScrollConceptAutocomplete";
import { aiPlanFilterFieldSx } from "../../../constants/regulationDetailsNavTabStyles";
import { useBrandThemeColors } from "../../../branding";
import { useAutoTaggingPlanTypes } from "../../../hooks/useAutoTaggingPlanTypes";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import {
  fetchConditionalPlanOutputConcepts,
  fetchConfigurableConcepts,
  formatPlanTypeCode,
  getConceptDisplayLabel,
  type ConfigurableConcept,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";

const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const CONCEPT_PAGE_SIZE = 20;
const CASCADE_COLUMN_HEIGHT = 220;

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

const cascadeColumnHeaderSx = {
  px: 2,
  py: 1,
  fontSize: "0.875rem",
  fontWeight: 700,
  color: "#374151",
  borderBottom: "1px solid #E5E7EB",
  bgcolor: "#fff",
  flexShrink: 0,
} as const;

const cascadeColumnScrollSx = {
  height: CASCADE_COLUMN_HEIGHT,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: "#C4C4C4 transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#C4C4C4",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
} as const;

export type EnhancedTaggingThenTag = {
  dimensionCode: string;
  dimensionLabel: string;
  valueCode: string;
  valueLabel: string;
};

type EnhancedTaggingRuleThenStepProps = Readonly<{
  value: EnhancedTaggingThenTag;
  onChange: (next: EnhancedTaggingThenTag) => void;
  /** API `target_type` — Object column + output concepts are scoped to this. */
  targetType: string;
  /** Optional subtype passed to output-concepts when the object requires one. */
  targetSubType?: string;
  showValidation?: boolean;
}>;

type ColumnRowProps = Readonly<{
  label: string;
  selected: boolean;
  onClick: () => void;
  /** Leaf columns (no next level) hide the chevron. */
  showChevron?: boolean;
}>;

function ColumnRow({
  label,
  selected,
  onClick,
  showChevron = true,
}: ColumnRowProps) {
  const { primaryColor } = useBrandThemeColors();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        minHeight: 44,
        border: "none",
        borderLeft: selected
          ? `3px solid ${primaryColor}`
          : "3px solid transparent",
        textAlign: "left",
        px: 2,
        py: 1.25,
        cursor: "pointer",
        bgcolor: selected ? `${primaryColor}14` : "transparent",
        color: selected ? primaryColor : "#374151",
        fontSize: "0.875rem",
        fontWeight: selected ? 600 : 400,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          bgcolor: selected ? `${primaryColor}1F` : "#F9FAFB",
        },
      }}
    >
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {label}
      </Box>
      {showChevron ? (
        <ChevronRightIcon
          sx={{ fontSize: 16, color: selected ? primaryColor : "#9CA3AF" }}
        />
      ) : null}
    </Box>
  );
}

function sameConceptCode(left: string, right: string): boolean {
  const normalizedLeft = left.trim().toLowerCase();
  const normalizedRight = right.trim().toLowerCase();
  return Boolean(normalizedLeft) && normalizedLeft === normalizedRight;
}

function formatImpactedDimensionLabel(
  objectName: string,
  dimensionLabel: string,
): string {
  const objectPart = objectName.trim();
  const dimensionPart = dimensionLabel.trim();
  // Only show a committed selection once Dimension is chosen — do not
  // prefill the trigger with Object alone from the prior step.
  if (!dimensionPart) return "";
  if (objectPart) return `${objectPart}:${dimensionPart}`;
  return dimensionPart;
}

export default function EnhancedTaggingRuleThenStep({
  value,
  onChange,
  targetType,
  targetSubType = "",
  showValidation = false,
}: EnhancedTaggingRuleThenStepProps) {
  const { primaryColor } = useBrandThemeColors();
  const { objectTypeOptions } = useAutoTaggingPlanTypes({
    configurableOnly: true,
  });

  const trimmedTargetType = targetType.trim();
  const hasTargetType = Boolean(trimmedTargetType);

  const objectOption = useMemo(() => {
    if (!hasTargetType) return null;
    const matched = objectTypeOptions.find(
      (item) =>
        item.code.trim().toLowerCase() === trimmedTargetType.toLowerCase(),
    );
    return {
      code: trimmedTargetType,
      name:
        matched?.name?.trim() ||
        formatPlanTypeCode(trimmedTargetType) ||
        trimmedTargetType,
    };
  }, [hasTargetType, objectTypeOptions, trimmedTargetType]);

  const [conceptOptions, setConceptOptions] = useState<ConfigurableConcept[]>(
    [],
  );
  const [conceptsLoading, setConceptsLoading] = useState(false);
  const [conceptsError, setConceptsError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [draftDimensionCode, setDraftDimensionCode] = useState(
    value.dimensionCode,
  );

  const open = Boolean(anchorEl);
  /** Bumped on open / Try Again so dimensions refetch for freshness. */
  const [conceptsReloadToken, setConceptsReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Fetch only while the cascade popover is open — not when the wizard mounts.
    if (!open || !hasTargetType) {
      setConceptsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setConceptsLoading(true);
    setConceptsError(null);
    const subType = targetSubType.trim() || null;
    void (async () => {
      try {
        let items: ConfigurableConcept[] = [];
        try {
          items = await fetchConditionalPlanOutputConcepts(
            trimmedTargetType,
            subType,
          );
        } catch {
          items = [];
        }
        if (cancelled) return;
        if (items.length === 0) {
          items = await fetchConfigurableConcepts(trimmedTargetType, subType);
        }
        if (cancelled) return;
        setConceptOptions(items);
      } catch {
        if (!cancelled) {
          setConceptOptions([]);
          setConceptsError("Couldn't load dimensions.");
        }
      } finally {
        if (!cancelled) setConceptsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    conceptsReloadToken,
    hasTargetType,
    open,
    targetSubType,
    trimmedTargetType,
  ]);

  const dimensionMenuItems = useMemo(() => {
    const items = conceptOptions.map((opt) => ({
      code: opt.code,
      name: getConceptDisplayLabel(opt),
    }));
    const savedCode = value.dimensionCode.trim();
    if (!savedCode) return items;
    if (items.some((item) => sameConceptCode(item.code, savedCode))) {
      return items;
    }
    return [
      {
        code: savedCode,
        name: value.dimensionLabel.trim() || savedCode,
      },
      ...items,
    ];
  }, [conceptOptions, value.dimensionCode, value.dimensionLabel]);

  const {
    input,
    setInput,
    options,
    loading: valuesLoading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  } = usePaginatedConceptValues(value.dimensionCode, CONCEPT_PAGE_SIZE);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((searchText?: string) => {
        void loadOptions(searchText);
      }, CONCEPT_SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  const handleValueInputChange = useCallback(
    (_event: unknown, nextValue: string) => {
      if (nextValue === input) return;
      setInput(nextValue);
      if (!nextValue.trim()) {
        debouncedLoadOptions.cancel();
        void loadOptions(undefined);
        return;
      }
      debouncedLoadOptions(nextValue);
    },
    [debouncedLoadOptions, input, loadOptions, setInput],
  );

  const selectedValues = useMemo<ConceptValue[]>(() => {
    if (!value.valueCode.trim()) return [];
    return [
      {
        id: value.valueCode,
        code: value.valueCode,
        name: value.valueLabel || value.valueCode,
      },
    ];
  }, [value.valueCode, value.valueLabel]);

  const displayOptions = useMemo(() => {
    if (selectedValues.length === 0) return options;
    const codes = new Set(options.map((item) => item.code));
    const missing = selectedValues.filter((item) => !codes.has(item.code));
    return missing.length === 0 ? options : [...missing, ...options];
  }, [options, selectedValues]);

  const searchLower = search.trim().toLowerCase();

  const objectVisible = useMemo(() => {
    if (!objectOption) return false;
    if (!searchLower) return true;
    if (objectOption.name.toLowerCase().includes(searchLower)) return true;
    // Keep Object visible when a Dimension under it matches search.
    return dimensionMenuItems.some((item) =>
      item.name.toLowerCase().includes(searchLower),
    );
  }, [dimensionMenuItems, objectOption, searchLower]);

  const filteredDimensions = useMemo(() => {
    if (!searchLower) return dimensionMenuItems;
    return dimensionMenuItems.filter((item) =>
      item.name.toLowerCase().includes(searchLower),
    );
  }, [dimensionMenuItems, searchLower]);

  const selectedDimensionLabel =
    dimensionMenuItems.find((item) =>
      sameConceptCode(item.code, value.dimensionCode),
    )?.name ?? value.dimensionLabel;

  const displayValue = formatImpactedDimensionLabel(
    objectOption?.name ?? "",
    selectedDimensionLabel,
  );

  const dimensionError =
    showValidation && !value.dimensionCode.trim()
      ? "Please select a dimension."
      : undefined;
  const valueError =
    showValidation && !value.valueCode.trim()
      ? "Please select a value."
      : undefined;

  const fieldSx = { ...aiPlanFilterFieldSx, width: "100%" };
  const canConfirm = Boolean(draftDimensionCode.trim());

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!hasTargetType) return;
    setDraftDimensionCode(value.dimensionCode);
    setSearch("");
    setConceptsReloadToken((token) => token + 1);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleClearAll = () => {
    setDraftDimensionCode("");
    setSearch("");
    onChange({
      dimensionCode: "",
      dimensionLabel: "",
      valueCode: "",
      valueLabel: "",
    });
    handleClose();
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    const matched = dimensionMenuItems.find((item) =>
      sameConceptCode(item.code, draftDimensionCode),
    );
    onChange({
      dimensionCode: draftDimensionCode.trim(),
      dimensionLabel: matched?.name ?? draftDimensionCode.trim(),
      valueCode: "",
      valueLabel: "",
    });
    handleClose();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box>
        <Typography sx={formFieldLabelSx}>
          Impacted Object Type And Dimensions
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          disabled={!hasTargetType}
          onClick={handleOpen}
          endIcon={
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
          }
          aria-label="Impacted Object Type And Dimensions"
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            justifyContent: "space-between",
            height: 40,
            px: 1.5,
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 400,
            color: displayValue ? "#262626" : "#9ca3af",
            borderColor: dimensionError ? "error.main" : "#D1D5DB",
            bgcolor: "#fff",
            "&:hover": {
              borderColor: dimensionError ? "error.main" : "#9CA3AF",
              bgcolor: "#fff",
            },
            "&.Mui-disabled": {
              bgcolor: "#F9FAFB",
              borderColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
            }}
          >
            {displayValue || "Select object type and dimensions"}
          </Box>
        </Button>
        {dimensionError ? (
          <FormHelperText error sx={{ mx: 1.75 }}>
            {dimensionError}
          </FormHelperText>
        ) : null}

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: 462,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "8px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                overflow: "hidden",
              },
            },
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: "1px solid #E5E7EB" }}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Input dimension or value..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon
                      sx={{ fontSize: 18, color: "#9CA3AF" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              height: CASCADE_COLUMN_HEIGHT + 37,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                borderRight: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={cascadeColumnHeaderSx}>Object</Typography>
              <Box sx={cascadeColumnScrollSx}>
                {!objectOption || !objectVisible ? (
                  <Typography
                    sx={{ p: 1.5, fontSize: "0.8125rem", color: "#9CA3AF" }}
                  >
                    {hasTargetType
                      ? "No matching objects"
                      : "Select Reference Items first"}
                  </Typography>
                ) : (
                  <ColumnRow
                    label={objectOption.name}
                    selected
                    onClick={() => undefined}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Typography sx={cascadeColumnHeaderSx}>Dimension</Typography>
              <Box sx={cascadeColumnScrollSx}>
                {conceptsLoading ? (
                  <Typography
                    sx={{ p: 1.5, fontSize: "0.8125rem", color: "#9CA3AF" }}
                  >
                    Loading…
                  </Typography>
                ) : conceptsError ? (
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: "#9CA3AF" }}>
                      {conceptsError}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() =>
                        setConceptsReloadToken((token) => token + 1)
                      }
                      sx={{ mt: 0.5, px: 0, textTransform: "none" }}
                    >
                      Try Again
                    </Button>
                  </Box>
                ) : filteredDimensions.length === 0 ? (
                  <Typography
                    sx={{ p: 1.5, fontSize: "0.8125rem", color: "#9CA3AF" }}
                  >
                    {dimensionMenuItems.length === 0
                      ? "No dimensions for this object"
                      : "No matching dimensions"}
                  </Typography>
                ) : (
                  filteredDimensions.map((item) => (
                    <ColumnRow
                      key={item.code}
                      label={item.name}
                      selected={sameConceptCode(draftDimensionCode, item.code)}
                      onClick={() => setDraftDimensionCode(item.code)}
                      showChevron={false}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1.25,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Button
              size="small"
              startIcon={<FilterAltOffOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleClearAll}
              sx={{
                textTransform: "none",
                color: "#EF4444",
                fontSize: "0.8125rem",
                "&:hover": { bgcolor: "#FEF2F2" },
              }}
            >
              Clear all
            </Button>
            <IconButton
              aria-label="Confirm impacted object type and dimensions"
              onClick={handleConfirm}
              disabled={!canConfirm}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "6px",
                bgcolor: canConfirm ? primaryColor : "#E5E7EB",
                color: "#fff",
                "&:hover": {
                  bgcolor: canConfirm ? primaryColor : "#E5E7EB",
                },
                "&.Mui-disabled": {
                  bgcolor: "#E5E7EB",
                  color: "#fff",
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Popover>
      </Box>

      <Box>
        <Typography sx={formFieldLabelSx}>
          Updated Value
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <InfiniteScrollConceptAutocomplete
          label=""
          value={selectedValues}
          onChange={(_event, nextValue) => {
            const picked = nextValue[0];
            onChange({
              ...value,
              valueCode: picked?.code ?? "",
              valueLabel: picked ? picked.name || picked.code : "",
            });
          }}
          options={displayOptions}
          inputValue={input}
          onInputChange={handleValueInputChange}
          loading={valuesLoading}
          hideSelectedWhenSearching
          selectedVisibilityMode="matchWhenSearchingAllWhenEmpty"
          hasMoreNext={hasMoreNext}
          loadingMore={loadingMore}
          onLoadMoreNext={loadNext}
          onLoadMorePrev={loadPrev}
          maxSelection={1}
          hideSelectionCount
          placeholder="Select value"
          disabled={!value.dimensionCode.trim()}
          fullWidth
          error={Boolean(valueError)}
          helperText={valueError}
          sx={fieldSx}
        />
      </Box>
    </Box>
  );
}

export function createEmptyThenTag(): EnhancedTaggingThenTag {
  return {
    dimensionCode: "",
    dimensionLabel: "",
    valueCode: "",
    valueLabel: "",
  };
}

export function isThenTagComplete(tag: EnhancedTaggingThenTag): boolean {
  return Boolean(tag.dimensionCode.trim() && tag.valueCode.trim());
}
